<template>

  <li class="voicemail-item" v-if="!deleted">
    <div class="unread-status">
      <status-indicator
        v-if="!markedRead"
        status="active">
      </status-indicator>
    </div>
    <div class="voicemail-source" @click="toggleActions">
      <div class="call-number">
        {{ callerIdName }}
      </div>
      <div class="call-name">
        {{ numberFormatter(callerIdNum) }}
      </div>
    </div>
    <div class="vm-body flex align-center justify-space-between ml-10">
      <div class="call-info">
        <div class="call-time">
          {{ dateFormatter(time) }}
        </div>
        <div class="call-duration">
          {{durationFormatter(duration)}}
        </div>
      </div>
      <div class="voicemail-actions">
        <el-button
          class="table-action-button"
          @click="streamVoicemail"
          v-if="!playActive"
          icon="el-icon-caret-right">
        </el-button>
        <el-button
          class="table-action-button"
          v-loading="fileLoading"
          @click="pauseVoicemail"
          v-if="playActive"
          icon="mdi mdi-pause">
        </el-button>
        <el-button
          class="table-action-button"
          @click="deleteVoicemail"
          icon="el-icon-delete">
        </el-button>
      </div>
    </div>

    <div class="voicemail-av" v-show="audioURL.length && isStreaming">
      <div :id="'vm-audio-'+id">
        <audio src=""></audio>
      </div>
      <div class="vm-audio-progress">
        <progress
          :id="'progress-bar-'+id"
          value="0"
          max="1">
        </progress>
        <div class="black-text time-left-text">
          {{timeLeft}}
        </div>
      </div>
    </div>
  </li>


</template>

<script>

  import moment from 'moment-timezone';
  import feathersClient from "@/feathers/feathers-client";
  import {mapGetters} from "vuex";
  import {phoneNumberFormatter} from "@/globals/globals";

  export default {
    name: 'Voicemail',
    props: {
      id: {required: true},
      time: {required: true},
      duration: {required: true},
      callerIdName: {required: true},
      callerIdNum: {required: true},
      status: {required: true},
      dir: {required: true}
    },
    computed: {
      ...mapGetters('voicemails', {getVoicemailBox: 'get'}),
      user(){
        return this.$store.state.auth.user;
      }
    },
    data() {
      return {
        playActive: false,
        markedRead: this.status !== 'unread',
        deleted: false,
        isStreaming: false,
        isPaused: false,
        audioURL: '',
        audioObject: {},
        fileLoading: false,
        fileRetrieved: false,
        timeLeft: null,
        actionsVisible: false
      }
    },
    methods: {
      durationFormatter(time) {
        const hours = Math.floor(time / 3600);
        const remainderSeconds = time % 3600;
        const minutes = Math.floor(remainderSeconds / 60);
        const seconds = Math.round(remainderSeconds % 60);

        if (hours > 0) return hours + 'hr ' + minutes + 'm';
        if (minutes > 0) return minutes + 'm';
        if (seconds > 0) return seconds + 's';
        return '0';
      },
      timeLeftFormatter(time){
        const remainderSeconds = time % 3600;
        let minutes = Math.floor(remainderSeconds / 60);
        let seconds = Math.round(remainderSeconds % 60);

        if (minutes > 0) {
          if(minutes < 10){
            minutes = '0' + minutes;
          }

          if(seconds < 10){
            seconds = '0' + seconds;
          }

          return minutes + ':' + seconds;
        }

        if (seconds > 0) {

          if(seconds < 10){
            seconds = '0' + seconds;
          }

          return '00:' + seconds;
        }

        return '00:00';
      },
      dateFormatter(time) {
        const {timezone} = this.user;

        //unix from server. need to use UTC offset instead.
        const unixTime = moment.unix(time).utc();
        const today = moment.tz(timezone);
        const offset = today.utcOffset();
        const minuteOffset = Math.abs(today.utcOffset());
        const dayToday = today.format('YYYY-MM-DD');

        //day message occurred using server timestamp and user offset
        const dayCall = offset < 0
          ? moment(unixTime).subtract(minuteOffset, 'minutes').format('YYYY-MM-DD')
          : moment(unixTime).add(minuteOffset, 'minutes').format('YYYY-MM-DD');

        const dayDifference = moment.duration(moment(dayToday).diff(moment(dayCall))).asDays();

        //message happened today
        if(dayDifference === 0){
          return offset < 0
            ? moment(unixTime).subtract(minuteOffset, 'minutes').format('h:mm A')
            : moment(unixTime).add(minuteOffset, 'minutes').format('h:mm A');
        }

        //message happened yesterday
        if(dayDifference === 1) return 'Yesterday';

        //message happened in the recent week
        if(dayDifference >= 2 && dayDifference <= 6){
          return offset < 0
            ? moment(unixTime).subtract(minuteOffset, 'minutes').format('dddd')
            : moment(unixTime).add(minuteOffset, 'minutes').format('dddd');
        }

        //message is older than a week
        return offset < 0
          ? moment(unixTime).subtract(minuteOffset, 'minutes').format('M/D')
          : moment(unixTime).add(minuteOffset, 'minutes').format('M/D');
      },
      numberFormatter(number) {
        return number ? phoneNumberFormatter(number) : number;
      },
      streamVoicemail() {
        this.playActive = true;

        if (!this.isStreaming && !this.fileRetrieved) {
          this.fileLoading = true;
          feathersClient.service('voicemail-messages').get(this.id).then(res => {
          if (res.recording=== null) {
            this.$notify({
              title: 'Not Found',
              message: 'File Not Found.',
              type: 'warning'
            });
            this.fileLoading  = false;
            } else {
            this.audioURL = res.recording;
            this.audioObject = document.getElementById('vm-audio-' + this.id).querySelector('audio');
            this.audioObject.src = this.audioURL;
            this.fileLoading = false;
            this.audioObject.play();
            this.isStreaming = true;
            this.fileRetrieved = true;

            this.audioObject.addEventListener('ended', () => {
              this.playActive = false;
              this.isStreaming = false;
              this.isPaused = false;
            });

            this.audioObject.addEventListener('timeupdate', () => {
              this.initProgressBar();
            });
            }
          }).catch( () => true);
        }

        if (this.isStreaming) {
          this.audioObject.play();
        }

        if (!this.isStreaming && this.fileRetrieved) {
          this.audioObject.play();
          this.playActive = true;
          this.isStreaming = true;
        }

        if (!this.markedRead && this.status === 'unread') {
          this.updateVoicemail()
        }

      },
      initProgressBar() {
        const length = this.audioObject.duration ? this.audioObject.duration : 0;
        const current_time = this.audioObject.currentTime;
        if(length){
          this.timeLeft = this.timeLeftFormatter(length - current_time);
        }

        let progressBar = document.getElementById('progress-bar-' + this.id);
        progressBar.value = length ? current_time / length : length
        progressBar.addEventListener('click', (event) => {
          const percent = event.offsetX / progressBar.offsetWidth;
          this.audioObject.currentTime = percent * length;
          progressBar.value = percent / 100;
        });
      },
      pauseVoicemail() {
        this.playActive = false;
        if (this.isStreaming) {
          this.audioObject.pause();
          this.isPaused = true;
        }
      },
      updateVoicemail() {

        //check the voicemail messages store for msg numbers with Old so we are sure to update correctly.
        let lastOldMessageNum = 0;
        if(this.lastVMNumber){
          lastOldMessageNum = this.lastVMNumber
        } else{
          const mailbox = this.getVoicemailBox(this.user.myExtension.voicemailId);
          for(let i = 0; i < mailbox.messages.length; i++){
            if(mailbox.messages[i].status === 'read'){
              lastOldMessageNum = lastOldMessageNum < mailbox.messages[i].msgnum ? mailbox.messages[i].msgnum : lastOldMessageNum;
            }
          }
        }

        //this is to mark read voicemails as unread
        const contextString = this.dir.split('/INBOX')[0];
        const context = contextString + '/Old';

        //patch the actual vm message in db
        feathersClient.service('voicemail-messages').patch(this.id, {
          dir: context,
          msgnum: lastOldMessageNum + 1,
          mailboxuser: this.user.myExtension.number,
          mailboxContext: this.user.myExtension.tenantCode
        }).then(() => {
          this.$store.commit('setLastVMNumber', lastOldMessageNum + 1);
          this.markedRead = true;
          this.$store.commit('subtractVoicemailCount');
        });
      },
      deleteVoicemail() {
        this.$confirm('Delete Voicemail?', 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'Cancel'
        }).then(() => {
          feathersClient.service('voicemail-messages').remove(this.id, {
            query: {
              mailboxuser: this.user.myExtension.number,
              mailboxContext: this.user.myExtension.tenantCode
            }
          }).then(() => {
            this.deleted = true;
            if(!this.markedRead){
              this.$store.commit('subtractVoicemailCount');
            }
          });
        }).catch(() => true);

      },
      toggleActions() {
        this.actionsVisible = !this.actionsVisible;
      }
    },
    mounted(){
      if(this.status === 'unread' && !this.markedRead){
        this.$store.commit('addVoicemailCount');
      }
    }
  }

</script>

<style lang="scss" scoped>

  @import '../../../../../assets/scss/variables';

  .voicemail-item{
    padding-left: 0;
    padding-right: 0;
    justify-content: space-between;
    >*{
      flex: 1;
    }
    .unread-status{
      max-width: 20px;
    }
  }

  .unread-status {
    min-width: 20px;
  }

  .call-number, .call-name {
    margin-left: 10px;
  }

  .call-name {
    font-size: 10pt;
  }

  .call-duration {
    font-size: 10pt;
  }

  .call-info {
    text-align: left;
    margin-right: 10px;
  }

  .vm-audio{
    canvas{
      display: none !important;
    }
  }

  .voicemail-source{
    width: 200px;
    max-width: 200px;
    cursor: pointer;
  }

  .vm-audio-progress{
    margin-left: 20px;
    display: flex;
    align-items: center;
  }

  .time-left-text{
    margin-left: 10px;
  }

  progress[value] {
    /* Reset the default appearance */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    width: 150px;
    height: 10px;
  }

  progress[value]::-webkit-progress-bar {
    background-color: #eee;
    border-radius: 50px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25) inset;
  }

  progress[value]::-moz-progress-bar {
    background-color: #eee;
    border-radius: 50px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25) inset;
  }

  progress[value]::-webkit-progress-value {
    background: transparentize($voxo-blue, 0.2);
    border-radius: 50px;
  }

  @media (max-width: 959px){
    progress[value] {
      width: 200px;
      height: 10px;
    }

    .voicemail-actions, .voicemail-av{
      display: flex;
      flex-basis: 100%;
      margin-left: 25px;
    }

    .vm-audio-progress{
      margin-top: 8px;
      margin-left: 5px;
    }

    .voicemail-item{
      flex-wrap: wrap;
    }

    .voicemail-source{
      width: 150px;
    }

    .call-info{
      margin-right: 5px;
    }
  }

</style>
